import React, { useState, useEffect, useMemo, useRef } from "react";
import { Flex, Text, Select, IconButton, Button, Dialog } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard } from "components";
// Real hooks (commented out until endpoints are available)
import {
  useGetKlaviyoMappings,
  useGetKlaviyoLists,
  useSaveKlaviyoMappings,
  useValidateKlaviyo,
  useDeactivateKlaviyo,
  useGetAllBrands,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context"; 
import { useToast } from "../../../context/ToastContext";
import { PermissionAlertDialog } from "components";

export function ActiveKlaviyoIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Check if user is owner
  const isOwner = userProfile.user_role === "Owner";

  // validation hook + api key UI state
  const validateKlaviyo = useValidateKlaviyo();
  const [apiKey, setApiKey] = useState("");
  const [isApiSaving, setIsApiSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasStoredApiKey, setHasStoredApiKey] = useState(false); // whether there's a saved (but masked) key

  // Inline validation state (for field-level UX)
  const [isApiKeyValid, setIsApiKeyValid] = useState(true);
  const [apiValidationMessage, setApiValidationMessage] = useState(null);
  const [apiSaveSuccess, setApiSaveSuccess] = useState(false);

  // separate loading flags for each save
  // const [isLocationSaving, setIsLocationSaving] = useState(false);
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
const [isIndependentSaving, setIsIndependentSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id)

  // Klaviyo resource / mappings
  const { data: klaviyoListsData, isLoading: isResourcesLoading } = useGetKlaviyoLists(userProfile?.organisation_id);
  const { data: klaviyoMappingsData, isLoading: isMappingsLoading } = useGetKlaviyoMappings(userProfile?.organisation_id);

  // save / deactivate
  const saveKlaviyoMappings = useSaveKlaviyoMappings();
  const deactivateKlaviyo = useDeactivateKlaviyo();

  // Local lists / state
  const [klaviyoLists, setKlaviyoLists] = useState([]); // normalized lists: { id, name }
  const [bconnectBrands, setBconnectBrands] = useState([]); // non-default brands
  const [independentLocations, setIndependentLocations] = useState([]);

  // single mapping state keyed by bconnect_location_id -> { klaviyo_list_id, isMapped }
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  // Per-brand temporary selection for "apply to brand" (optional usage in this screen)
  const [brandListSelections, setBrandListSelections] = useState({});

  const handleApiKeyChange = (e) => {
    const value = e.target.value ?? "";
    setApiKey(value);
    setIsApiKeyValid(true);
    setApiValidationMessage(null);
  };

  const originalLocationMappingsRef = useRef(originalLocationMappings);
  useEffect(() => {
    originalLocationMappingsRef.current = originalLocationMappings;
  }, [originalLocationMappings]);

  useEffect(() => {
    const raw = klaviyoListsData?.data.lists || [];
    const normalized = (raw || []).map((l) => ({
      id: String(l.id ?? ""),
      name: l.name ?? "",
    }));

    setKlaviyoLists(normalized);
  }, [klaviyoListsData]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = (brandsData || []).find((b) => b.is_default);
      const nonDefault = (brandsData || []).filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = (locationsData || []).filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    } else {
      setBconnectBrands([]);
      setIndependentLocations([]);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Also provide a flat list of all brand-owned locations
  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  useEffect(() => {
    const masked = klaviyoMappingsData?.data?.api_key ?? "";
    // put masked string into the input so the view shows it
    setApiKey(masked);
    // flag that there's a stored (but masked) key
    setHasStoredApiKey(Boolean(masked && masked.includes("*")));
    // reset validation UI
    setIsApiKeyValid(true);
    setApiValidationMessage(null);
  }, [klaviyoMappingsData]);

useEffect(() => {
  // if there's no server payload, clear both local and original states
  if (!klaviyoMappingsData) {
    setLocationMappings({});
    setOriginalLocationMappings({});
    originalLocationMappingsRef.current = {};
    return;
  }

  // build a serverMap: { "<locId>": { klaviyo_list_id: "..." } }
  const lm = klaviyoMappingsData.data.mappings.location_mappings || [];
  const serverMap = {};
  lm.forEach((m) => {
    const key = String(m.bconnect_location_id);
    serverMap[key] = { klaviyo_list_id: m.klaviyo_list_id || "" };
  });

  // Merge server values into local editable state but preserve local unsaved edits.
  setLocationMappings((prevLoc = {}) => {
    const prevOrig = originalLocationMappingsRef.current || {};
    const next = { ...prevLoc };

    Object.keys(serverMap).forEach((key) => {
      const serverEntry = serverMap[key];
      const prevEntry = prevLoc[key];
      const prevOrigEntry = prevOrig[key] || { klaviyo_list_id: "" };

      const prevLocList = String(prevEntry?.klaviyo_list_id || "");
      const prevOrigList = String(prevOrigEntry.klaviyo_list_id || "");

      // If there's a local unsaved edit (prevLoc !== prevOrig), keep it.
      // Otherwise adopt the server value for this key.
      if (!prevEntry || prevLocList === prevOrigList) {
        next[key] = {
          klaviyo_list_id: serverEntry.klaviyo_list_id,
          isMapped: Boolean(serverEntry.klaviyo_list_id),
        };
      }
    });

    return next;
  });

  // Merge server values into originals (only overwrite keys returned by server).
  setOriginalLocationMappings((prev = {}) => {
    const merged = { ...prev };
    Object.keys(serverMap).forEach((k) => {
      merged[k] = { klaviyo_list_id: serverMap[k].klaviyo_list_id };
    });
    // keep ref in sync synchronously
    originalLocationMappingsRef.current = merged;
    return merged;
  });
}, [klaviyoMappingsData]);

  // Handlers
  const handleLocationKlaviyoListChange = (bconnectLocationId, klaviyoListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { klaviyo_list_id: "", isMapped: false };
      next[key] = { ...existing, klaviyo_list_id: klaviyoListId, isMapped: Boolean(klaviyoListId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => {
      const next = { ...(prev || {}) };
      next[String(brandId)] = listId;
      return next;
    });
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { klaviyo_list_id: "", isMapped: false };
        next[key] = { ...existing, klaviyo_list_id: selectedList, isMapped: Boolean(selectedList) };
      });
      return next;
    });
  };

  const handleSaveApiKey = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      setApiSaveSuccess(false);
      return;
    }

    // If the input currently contains masking chars, treat as "no change" and do nothing.
    if (apiKey && apiKey.includes("*")) {
      setIsApiKeyValid(false);
      setApiValidationMessage("Please enter a new API key.");
      setApiSaveSuccess(false);
      setToastMessage("No changes to API key");
      return;
    }

    // If empty and there was a stored (masked) key, also treat as "no change"
    if (!apiKey && hasStoredApiKey) {
      setIsApiKeyValid(false);
      setApiValidationMessage("Please enter a valid API key.");
      setApiSaveSuccess(false);
      setToastMessage("No changes to API key");
      return;
    }

    // Now require a non-empty key
    if (!apiKey || apiKey.trim() === "") {
      setIsApiKeyValid(false);
      setApiValidationMessage("Please enter a valid API key.");
      setApiSaveSuccess(false);
      setToastMessage("API key cannot be empty");
      return;
    }

    // user entered a (new) key -> validate/save
    setIsApiSaving(true);
    setIsApiKeyValid(true);
    setApiValidationMessage(null);

    try {
      const result = await validateKlaviyo.mutateAsync({
        api_key: apiKey.trim(),
        organisation_id: userProfile.organisation_id,
      });

      if (result?.data?.status === "success") {
        // queryClient.invalidateQueries(["klaviyo-integration-status"]);
        queryClient.invalidateQueries(["klaviyo-mappings"]);
        setToastMessage("API key validated and saved");
        // clear the visible input (backend returns masked on next fetch)
        setApiKey("");
        setHasStoredApiKey(true);
        setIsApiKeyValid(true);
        setApiValidationMessage(null);
        setApiSaveSuccess(true);
      } else {
        const msg = "Invalid API key";
        setIsApiKeyValid(false);
        setApiValidationMessage(msg);
        setToastMessage(msg);
        setApiSaveSuccess(false);
      }
    } catch (err) {
      const msg = "Failed to validate API key";
      setIsApiKeyValid(false);
      setApiValidationMessage(msg);
      setToastMessage(msg);
      setApiSaveSuccess(false);
    } finally {
      setIsApiSaving(false);
    }
  };

  // Validation / dirty detection (brand-owned)
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];

    // must have at least one populated row that changed
    const hasAtLeastOne = brandLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { klaviyo_list_id: "" };
      const currList = String(curr.klaviyo_list_id || "");
      const origList = String(orig.klaviyo_list_id || "");
      if (!currList) return false;
      return currList !== origList;
    });

    return Boolean(hasAtLeastOne);
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  // independent locations dirty detection
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];
    const hasAtLeastOne = indep.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { klaviyo_list_id: "" };
      const currList = String(curr.klaviyo_list_id || "");
      const origList = String(orig.klaviyo_list_id || "");
      if (!currList) return false;
      return currList !== origList;
    });
    return Boolean(hasAtLeastOne);
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const handleSaveLocationMappings = async (section) => {
  // determine which saving setters to toggle (support "brands", "independent" and a fallback "both")
  const savingSetters =
    section === "brands"
      ? [setIsBrandsSaving]
      : section === "independent"
      ? [setIsIndependentSaving]
      : [setIsBrandsSaving, setIsIndependentSaving];

  // choose locations based on section (don't set saving flags yet — compute payload first)
  let selectedLocations = [];
  if (section === "brands") {
    selectedLocations = allBrandOwnedLocations || [];
  } else if (section === "independent") {
    selectedLocations = independentLocations || [];
  } else {
    selectedLocations = [
      ...(independentLocations || []),
      ...(allBrandOwnedLocations || []),
    ];
  }

  const locationPayload = (selectedLocations || [])
    .map((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currList = curr.klaviyo_list_id || null;
      const orig = originalLocationMappings[key] || {};
      const origList = orig.klaviyo_list_id || null;

      // require list to be present to save
      if (!currList) return null;
      // skip unchanged
      if (currList === origList) return null;

      const klObj = klaviyoLists.find((kl) => String(kl.id) === String(currList));
      return {
        bconnect_location_id: locId,
        bconnect_location_name: loc.name ?? loc.label ?? "",
        klaviyo_list_id: currList,
        klaviyo_list_name: klObj?.name || "",
      };
    })
    .filter(Boolean);

  if (locationPayload.length === 0) {
    setToastMessage("No location changes to save");
    return;
  }

  // set the appropriate saving flag(s)
  savingSetters.forEach((s) => s(true));
  try {
    const payload = {
      organisation_id: userProfile.organisation_id,
      brand_mappings: [],
      location_mappings: locationPayload,
    };

    const result = await saveKlaviyoMappings.mutateAsync(payload);
    if (result?.data?.status !== "success") {
      throw new Error(result?.data?.message || "Failed to save location mappings");
    }

    // update originals for only the saved locations
    const newOrig = { ...originalLocationMappings };
    locationPayload.forEach((p) => {
      newOrig[String(p.bconnect_location_id)] = {
        klaviyo_list_id: p.klaviyo_list_id,
      };
    });
    setOriginalLocationMappings(newOrig);

    queryClient.invalidateQueries(["klaviyo-mappings"]);
    setToastMessage("Location mappings saved successfully");
  } catch (err) {
    console.error("Error saving Klaviyo mappings:", err);
    setToastMessage(err?.message || "Failed to save location mappings");
  } finally {
    // clear the same saving flag(s)
    savingSetters.forEach((s) => s(false));
  }
};

  // Handle disconnect click with permission check
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  // Disconnect handler
  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      const result = await deactivateKlaviyo.mutateAsync({ organisation_id: userProfile.organisation_id });
      if (result?.data?.status === "success") {
        queryClient.invalidateQueries(["klaviyo-integration-status"]);
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Klaviyo integration");
      }
    } catch (err) {
      console.error("Error disconnecting Klaviyo:", err);
      setToastMessage(err?.message || "Failed to disconnect Klaviyo");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  // Loading message (keeps original phrasing but adapted)
  if (isBrandsLoading || isResourcesLoading || isMappingsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Klaviyo integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Klaviyo"
        subtitle="Map locations to Klaviyo lists"
        image_url="klaviyo.jpg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* API Key section */}
        <ActiveIntegrateCard.Section
          name="API key"
          subtitle="Update the Klaviyo API key used for this integration"
        >
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            
            {/* Row 1: Input + buttons */}
            <Flex direction="row" align="center" gap="2" style={{ width: "100%" }}>
              <input
                value={apiKey}
                onChange={handleApiKeyChange}
                type={showApiKey ? "text" : "password"}
                placeholder={hasStoredApiKey ? "Stored API key (hidden)" : "Enter Klaviyo API key"}
                aria-label="Klaviyo API key"
                disabled={!isOwner}
                style={{
                  flex: 1,
                  height: 36,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: `1px solid ${isApiKeyValid ? "var(--gray-a7)" : "var(--red-8)"}`,
                  boxSizing: "border-box",
                  background: isOwner ? "var(--white)" : "var(--gray-3)",
                }}
              />

              <Button size="1" variant="soft" onClick={() => setShowApiKey((s) => !s)}>
                {showApiKey ? "Hide" : "Show"}
              </Button>

              <Button
                size="1"
                variant="solid"
                color="blue"
                onClick={handleSaveApiKey}
                loading={isApiSaving}
                disabled={!isOwner || isApiSaving}
              >
                Save changes
              </Button>
            </Flex>

            {/* Row 2: Inline validation */}
            {!isApiKeyValid && (
              <Text size="1" weight="regular" color="red">
                {apiValidationMessage || "Please enter a valid API key."}
              </Text>
            )}
            {/* Row 2b: Success message */}
            {apiSaveSuccess && (
              <Text size="1" weight="medium" color="green">
                API key validated and saved successfully.
              </Text>
            )}

            {/* Row 3: Stored key hint */}
            {hasStoredApiKey && (
              <Text size="2" style={{ color: "var(--gray-a9)" }}>
                An API key is stored (hidden). Enter a new key to update it, or leave as-is.
              </Text>
            )}

            {/* Row 4: Subtle helper text */}
            <Text size="2" style={{ color: "var(--gray-a9)" }}>
              Changing the API key will validate it immediately. Existing mapping activation state will be preserved.
            </Text>
          </Flex>
        </ActiveIntegrateCard.Section>
        {/* BRANDS section: grouped bconnect brands with their locations */}
        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "48%" }}>Klaviyo list</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                    {/* Brand header */}
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                      <Text size="2" weight="medium" style={{ flex: "1 1 0%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        [{b.name}]
                      </Text>

                      {/* Show brand-level apply only when brand has locations */}
                      {(brandLocations || []).length > 0 && (
                        <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <Select.Root
                            value={brandListSelections[String(b.id)] || ""}
                            onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                            disabled={!isOwner}
                            style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                          >
                            <Select.Trigger placeholder="Apply list to brand..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                title={brandListSelections[String(b.id)] ? (klaviyoLists.find((kl) => String(kl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply list to brand..."}
                              >
                                {brandListSelections[String(b.id)] ? (klaviyoLists.find((kl) => String(kl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply list to brand..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {klaviyoLists.map((kl) => <Select.Item key={kl.id} value={kl.id} title={kl.name}>{kl.name}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>

                          <Button size="1" variant="soft" onClick={() => handleApplyBrandList(b.id)} disabled={!isOwner || !brandListSelections[String(b.id)]} style={{ flexShrink: 0 }}>
                            Apply
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    {/* Locations under the brand */}
                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={{ width: "48%", minWidth: 0 }}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name ?? loc.label ?? ""}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "48%", minWidth: 0 }}>
                              <Select.Root value={mapping.klaviyo_list_id || ""} onValueChange={(v) => handleLocationKlaviyoListChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                                <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}>
                                    {mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {klaviyoLists.map((kl) => <Select.Item key={kl.id} value={kl.id} title={kl.name}>{kl.name}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isBrandsSaving}
                disabled={!isOwner || !hasBrandSectionChanges || isBrandsSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        {/* LOCATIONS section (independent / default-brand locations) */}
        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to a Klaviyo list">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "48%" }}>Klaviyo list</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};

                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={{ width: "48%", minWidth: 0 }}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name ?? loc.label ?? ""}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "48%", minWidth: 0 }}>
                      <Select.Root value={mapping.klaviyo_list_id || ""} onValueChange={(v) => handleLocationKlaviyoListChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                        <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}>
                            {mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {klaviyoLists.map((kl) => <Select.Item key={kl.id} value={kl.id} title={kl.name}>{kl.name}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isIndependentSaving}
                disabled={!isOwner || !hasIndependentLocationChanges || isIndependentSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Confirm dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Klaviyo</Dialog.Title>
          <Dialog.Description size="2" mb="4">Customer data will stop syncing with Klaviyo.</Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
